import { memo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  Legend,
} from 'recharts';
import { BarChart3, PieChart, TrendingUp, Circle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '@/lib/utils/date';
import { ChartExportButton } from '@/components/ui/chart-export-button';

interface ChartRendererProps {
  attachment: {
    data: {
      type: string;
      title?: string;
      x_axis?: string;
      y_axis?: string;
      series?: Array<{
        name: string;
        data: Array<{ x: string; y: number }>;
      }>;
      segments?: Array<{ label: string; value: number }>;
      columns?: string[];
      data?: any[][];
    };
  };
}

const CHART_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#6366f1',
  '#14b8a6',
  '#eab308',
  '#f97316',
  '#ec4899',
  '#4b5563',
  '#84cc16',
];

// Enhanced tooltip component similar to analytics page
const EnhancedTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-background/95 border rounded-lg shadow-lg p-3 backdrop-blur-sm">
      <p className="text-sm font-medium text-muted-foreground mb-2">
        {typeof label === 'string' && label.includes('T') ? formatDate.fullDateTime(new Date(label)) : label}
      </p>
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center gap-3 mt-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
              {entry.unit && entry.unit}
            </span>
            <span className="text-xs text-muted-foreground">{entry.name}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const ChartRenderer = memo(({ attachment }: ChartRendererProps) => {
  const { data } = attachment;

  // Handle empty data gracefully
  if (!data || (!data.series && !data.segments && !data.data && !data.columns && data.type !== 'gauge')) {
    return (
      <Card className="p-4 text-center bg-gray-50/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50">
        <p className="text-gray-600 dark:text-gray-400 text-sm">No data available for visualization</p>
      </Card>
    );
  }

  // Gauge Chart (new type)
  if (data.type === 'gauge') {
    const value = (data as any).value || 0;
    const min = (data as any).min || 0;
    const max = (data as any).max || 100;
    const unit = (data as any).unit || '';
    const title = data.title || 'Gauge';
    const thresholds = (data as any).thresholds || [];

    // Ensure value is within min/max range
    const safeValue = Math.max(min, Math.min(max, value));

    // Create gauge segments based on thresholds
    const segments = [];
    let prevValue = min;

    for (const threshold of thresholds) {
      if (threshold.value > prevValue && threshold.value <= max) {
        segments.push({
          name: `${prevValue}-${threshold.value}`,
          value: threshold.value - prevValue,
          color: threshold.color,
        });
        prevValue = threshold.value;
      }
    }

    if (prevValue < max) {
      segments.push({
        name: `${prevValue}-${max}`,
        value: max - prevValue,
        color: thresholds.length > 0 ? thresholds[thresholds.length - 1].color : CHART_COLORS[0],
      });
    }

    if (segments.length === 0) {
      segments.push({
        name: `${min}-${max}`,
        value: max - min,
        color: CHART_COLORS[0],
      });
    }

    return (
      <Card className="mt-3 bg-gradient-to-b from-background to-background/50 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Circle className="h-4 w-4" />
              {title}
            </CardTitle>
            <ChartExportButton
              chartId={`ai-gauge-chart-${title.toLowerCase().replace(/\s+/g, '-')}`}
              filename={`ai-gauge-chart-${title.toLowerCase().replace(/\s+/g, '-')}`}
              metadata={{
                title: title,
                description: `Gauge chart visualization from AI assistant`,
                dateRange: formatDate.dateRange(new Date()),
                additionalInfo: {
                  'Chart Type': 'Gauge Chart',
                  'Current Value': `${safeValue.toFixed(1)} ${unit}`,
                  Range: `${min} - ${max} ${unit}`,
                  Generated: formatDate.fullDateTime(new Date()),
                },
              }}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center" id={`ai-gauge-chart-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="h-[200px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={segments}
                    cx="50%"
                    cy="80%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={1}
                    dataKey="value"
                  >
                    {segments.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-center">
              <div className="text-3xl font-bold text-foreground">
                {safeValue.toFixed(1)} <span className="text-base text-muted-foreground">{unit}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex justify-between w-full px-4">
                <span>{min}</span>
                <span>{max}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Table renderer
  if (data.type === 'table') {
    const columns: string[] = data.columns ?? [];
    const rows: any[][] = data.data ?? [];
    const title = data.title || 'Table';

    if (columns.length === 0 || rows.length === 0) {
      return (
        <Card className="p-4 text-center bg-gray-50/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50">
          <CardHeader>
            <CardTitle className="text-sm">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400 text-sm">No data available</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="overflow-hidden mt-3 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full border-collapse border text-sm">
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <th
                      key={idx}
                      className="border px-3 py-2 text-left whitespace-nowrap bg-gray-50/50 dark:bg-gray-800/50 font-medium"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ridx) => (
                  <tr key={ridx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
                    {row.map((cell, cidx) => (
                      <td key={cidx} className="border px-3 py-2 whitespace-nowrap">
                        {cell !== undefined && cell !== null ? String(cell) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Data list renderer
  if (data.type === 'data') {
    const rows = data.data ?? [];
    if (!Array.isArray(rows) || rows.length === 0) {
      return (
        <Card className="p-4 text-center bg-gray-50/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50">
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400 text-sm">No data available</p>
          </CardContent>
        </Card>
      );
    }

    const allKeys = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));

    return (
      <Card className="overflow-hidden mt-3 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{data.title || 'Data'}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full border-collapse border">
              <thead>
                <tr>
                  {allKeys.map((key) => (
                    <th key={key} className="border px-3 py-2 text-left bg-gray-50/50 dark:bg-gray-800/50 font-medium">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
                    {allKeys.map((key) => (
                      <td key={key} className="border px-3 py-2">
                        {(item as any)[key] !== undefined && (item as any)[key] !== null
                          ? String((item as any)[key])
                          : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Chart renderers
  const seriesList = data.series ?? [];
  if (seriesList.length === 0 || seriesList.every((s: any) => !s.data || s.data.length === 0)) {
    return (
      <div className="p-4 text-center bg-gray-50 rounded border">
        <p className="text-gray-600 text-sm">No chart data available</p>
      </div>
    );
  }

  // Merge data by timestamp for multi-series charts
  const mergeSeriesData = (series: any[]) => {
    const mergedDataMap: Record<string, any> = {};
    for (const s of series) {
      for (const point of s.data) {
        if (!mergedDataMap[point.x]) {
          mergedDataMap[point.x] = { x: point.x };
        }
        mergedDataMap[point.x][s.name] = point.y;
      }
    }
    return Object.values(mergedDataMap).sort((a: any, b: any) => new Date(a.x).getTime() - new Date(b.x).getTime());
  };

  // Line Chart
  if (data.type === 'line') {
    const mergedData = mergeSeriesData(seriesList);
    const chartId = `ai-line-chart-${data.title?.toLowerCase().replace(/\s+/g, '-') || 'chart'}`;

    return (
      <Card className="mt-3 bg-gradient-to-b from-background to-background/50 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {data.title}
            </CardTitle>
            <ChartExportButton
              chartId={chartId}
              filename={`ai-line-chart-${data.title?.toLowerCase().replace(/\s+/g, '-') || 'chart'}`}
              metadata={{
                title: data.title || 'Line Chart',
                description: `Line chart visualization from AI assistant`,
                dateRange: formatDate.dateRange(new Date()),
                additionalInfo: {
                  'Chart Type': 'Line Chart',
                  Generated: formatDate.fullDateTime(new Date()),
                  'Data Series': seriesList.length.toString(),
                },
              }}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64" id={chartId}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mergedData}>
                <defs>
                  {seriesList.map((_series: any, index: number) => (
                    <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                <XAxis
                  dataKey="x"
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<EnhancedTooltip />} cursor={{ stroke: 'hsl(var(--muted-foreground)/0.2)' }} />
                <Legend />
                {seriesList.map((series: any, index: number) => (
                  <Line
                    key={series.name}
                    type="monotone"
                    dataKey={series.name}
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Area Chart
  if (data.type === 'area') {
    const mergedData = mergeSeriesData(seriesList);

    return (
      <Card className="mt-3 bg-gradient-to-b from-background to-background/50 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            {data.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={mergedData}>
                <defs>
                  {seriesList.map((_series: any, index: number) => (
                    <linearGradient
                      key={`area-gradient-${index}`}
                      id={`area-gradient-${index}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.1} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                <XAxis
                  dataKey="x"
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<EnhancedTooltip />} cursor={{ stroke: 'hsl(var(--muted-foreground)/0.2)' }} />
                {seriesList.map((series: any, index: number) => (
                  <Area
                    key={series.name}
                    type="monotone"
                    dataKey={series.name}
                    stackId="1"
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    fill={`url(#area-gradient-${index})`}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Bar Chart
  if (data.type === 'bar') {
    const mergedData = mergeSeriesData(seriesList);

    return (
      <Card className="mt-3 bg-gradient-to-b from-background to-background/50 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {data.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mergedData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                <XAxis
                  dataKey="x"
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<EnhancedTooltip />} cursor={{ fill: 'hsl(var(--muted-foreground)/0.1)' }} />
                <Legend />
                {seriesList.map((series: any, index: number) => (
                  <Bar key={series.name} dataKey={series.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Scatter Chart
  if (data.type === 'scatter') {
    return (
      <Card className="mt-3 bg-gradient-to-b from-background to-background/50 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {data.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                <XAxis dataKey="x" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <YAxis dataKey="y" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <Tooltip content={<EnhancedTooltip />} cursor={{ stroke: 'hsl(var(--muted-foreground)/0.2)' }} />
                {seriesList.map((series: any, index: number) => (
                  <Scatter
                    key={series.name}
                    name={series.name}
                    data={series.data}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Pie Chart
  if (data.type === 'pie') {
    const segments = data.segments ?? [];
    if (!Array.isArray(segments) || segments.length === 0) {
      return (
        <Card className="p-4 text-center bg-gray-50/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50">
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400 text-sm">No pie chart data available</p>
          </CardContent>
        </Card>
      );
    }

    const pieData = segments.map((segment) => ({
      name: segment.label,
      value: segment.value,
    }));

    return (
      <Card className="mt-3 bg-gradient-to-b from-background to-background/50 border border-gray-200/50 dark:border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            {data.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                  label={({ name, percent }) => (percent > 0.05 ? `${name} (${(percent * 100).toFixed(1)}%)` : '')}
                  labelLine={false}
                >
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<EnhancedTooltip />} />
              </RechartsPieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 mt-2">
              {pieData.map((entry, index) => (
                <div key={index} className="flex items-center gap-1 text-xs">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span>
                    {entry.name}: {entry.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="p-4 text-center bg-gray-50/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50">
      <CardContent>
        <p className="text-gray-600 dark:text-gray-400 text-sm">Unsupported chart type: {data.type}</p>
      </CardContent>
    </Card>
  );
});

ChartRenderer.displayName = 'ChartRenderer';

export default ChartRenderer;
export { ChartRenderer };
